// Period and comparison ranges for Statistici V2. Days are "YYYY-MM-DD"
// strings in Bucharest time; the latest day ("hi") is today.

const DAY_MS = 24 * 60 * 60 * 1000;

export const MONTHS = ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie", "iulie", "august",
  "septembrie", "octombrie", "noiembrie", "decembrie"];
const MONTHS_SHORT = ["ian.", "feb.", "mar.", "apr.", "mai", "iun.", "iul.", "aug.", "sept.", "oct.", "nov.", "dec."];

export function todayBucharest() {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {timeZone: "Europe/Bucharest"}).format(new Date());
}

const toUtc = (day) => Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10));
const fromUtc = (ms) => new Date(ms).toISOString().slice(0, 10);

export const addDays = (day, n) => fromUtc(toUtc(day) + n * DAY_MS);
export const daysBetween = (a, b) => Math.round((toUtc(b) - toUtc(a)) / DAY_MS);
const lastDayOfMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate(); // month 1-12
const pad = (n) => String(n).padStart(2, "0");
const makeDay = (year, month, day) => `${year}-${pad(month)}-${pad(Math.min(day, lastDayOfMonth(year, month)))}`;

export const PERIODS = [
  {id: "today", label: "Azi"},
  {id: "7d", label: "7 zile"},
  {id: "30d", label: "30 zile"},
  {id: "custom", label: "Custom"},
];

export const COMPARISONS = [
  {id: "prev", label: "Perioada anterioară"},
  {id: "week", label: "Aceeași perioadă, cu o săptămână în urmă"},
  {id: "month", label: "Aceeași perioadă – luna la lună"},
  {id: "year", label: "Aceeași perioadă – anii trecuți"},
  {id: "custom", label: "Altă perioadă…"},
];

// The selected period [s, e].
export function periodRange({period, from, to}, hi) {
  if (period === "today") {
    return [hi, hi];
  }
  if (period === "30d") {
    return [addDays(hi, -29), hi];
  }
  if (period === "custom") {
    let s = from || addDays(hi, -6);
    let e = to || hi;
    if (e < s) {
      [s, e] = [e, s];
    }
    return [s, e];
  }
  return [addDays(hi, -6), hi];
}

// Months before s's month (newest first) back to the first month in the data.
export function monthOptions(s, dataFrom) {
  const options = [];
  let year = +s.slice(0, 4);
  let month = +s.slice(5, 7) - 1;
  const firstYear = dataFrom ? +dataFrom.slice(0, 4) : year - 2;
  const firstMonth = dataFrom ? +dataFrom.slice(5, 7) : 1;
  while (year > firstYear || (year === firstYear && month >= firstMonth)) {
    if (month < 1) {
      month = 12;
      year--;
      continue;
    }
    options.push({value: `${year}-${pad(month)}`, label: `${MONTHS[month - 1]} ${year}`});
    month--;
  }
  return options;
}

export function yearOptions(s, dataFrom) {
  const firstYear = dataFrom ? +dataFrom.slice(0, 4) : +s.slice(0, 4) - 2;
  const options = [];
  for (let year = +s.slice(0, 4) - 1; year >= firstYear; year--) {
    options.push(year);
  }
  return options;
}

// The comparison range: always as long as [s, e].
export function comparisonRange({cmp, cmpFrom, cmpMonth, cmpYear}, s, e, dataFrom) {
  const len = daysBetween(s, e) + 1;
  let start;
  if (cmp === "week") {
    start = addDays(s, -7);
  } else if (cmp === "month") {
    const chosen = cmpMonth || (monthOptions(s, dataFrom)[0] || {}).value;
    if (!chosen) {
      start = addDays(s, -len);
    } else {
      start = makeDay(+chosen.slice(0, 4), +chosen.slice(5, 7), +s.slice(8, 10));
    }
  } else if (cmp === "year") {
    const year = cmpYear || yearOptions(s, dataFrom)[0] || +s.slice(0, 4) - 1;
    start = makeDay(year, +s.slice(5, 7), +s.slice(8, 10)); // 29 Feb → 28 Feb
  } else if (cmp === "custom" && cmpFrom) {
    start = cmpFrom;
  } else {
    start = addDays(s, -len);
  }
  return [start, addDays(start, len - 1)];
}

// "17 – 23 oct." / "30 sept. – 6 oct. 2025" / "1 nov."
export function rangeLabel(a, b, today = todayBucharest()) {
  const part = (day, withMonth, withYear) => {
    const d = +day.slice(8, 10);
    const m = MONTHS_SHORT[+day.slice(5, 7) - 1];
    return `${d}${withMonth ? ` ${m}` : ""}${withYear ? ` ${day.slice(0, 4)}` : ""}`;
  };
  const otherYear = a.slice(0, 4) !== today.slice(0, 4) || b.slice(0, 4) !== today.slice(0, 4);
  if (a === b) {
    return part(a, true, otherYear);
  }
  const sameMonth = a.slice(0, 7) === b.slice(0, 7);
  return `${part(a, !sameMonth, otherYear && a.slice(0, 4) !== b.slice(0, 4))} – ${part(b, true, otherYear)}`;
}

export function dayLabel(day) {
  const d = new Date(toUtc(day));
  const weekday = ["Dum", "Lun", "Mar", "Mie", "Joi", "Vin", "Sâm"][d.getUTCDay()];
  return `${weekday} ${+day.slice(8, 10)} ${MONTHS_SHORT[+day.slice(5, 7) - 1]}`;
}
